import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import ModalOverlay from '../../../UI/ModalOverlay/ModalOverlay.tsx';
import LoadingSpinner from '../Shared/LoadingSpinner.tsx';
import Pagination from '../Shared/Pagination.tsx';
import {
  AdminAnalyticsFilters,
  UserActivityScope,
  UserActivityWindowDays,
} from '../../../../types/adminAnalytics.ts';
import {
  adminAnalyticsApi,
  triggerBlobDownload,
} from '../../../../services/adminAnalyticsApi.ts';
import {
  getActiveUsersDisclaimer,
  getLastLoginCompareWarning,
  USER_ACTIVITY_SCOPE_LABELS,
} from '../../../../utils/adminAnalyticsActiveUsers.ts';
import styles from './AnalyticsUserActivityModal.module.scss';

const PAGE_SIZE = 20;

const SCOPES: UserActivityScope[] = ['within', 'band', 'no_activity', 'inactive_over'];

interface AnalyticsUserActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricLabel: string;
  windowDays: UserActivityWindowDays;
  filters: AdminAnalyticsFilters;
  compareTo?: string;
}

function formatActivityRange(
  asOf: string,
  cutoffFrom: string,
  cutoffTo: string | null
): string {
  const asOfLabel = moment(asOf).format('DD/MM/YYYY HH:mm');
  const fromLabel = moment(cutoffFrom).format('DD/MM/YYYY HH:mm');
  if (cutoffTo) {
    return `${fromLabel} – ${moment(cutoffTo).format('DD/MM/YYYY HH:mm')}`;
  }
  return `Desde ${fromLabel} hasta ${asOfLabel}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return moment(value).format('DD/MM/YYYY HH:mm');
}

const AnalyticsUserActivityModal: React.FC<AnalyticsUserActivityModalProps> = ({
  isOpen,
  onClose,
  metricLabel,
  windowDays,
  filters,
  compareTo,
}) => {
  const [scope, setScope] = useState<UserActivityScope>('within');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof adminAnalyticsApi.fetchUserActivity>
  > | null>(null);

  const queryBase = useMemo(
    () => ({
      windowDays,
      scope,
      asOf: filters.to,
      includeDeleted: filters.includeDeleted,
      usersValidatedOnly: filters.usersValidatedOnly,
    }),
    [windowDays, scope, filters.to, filters.includeDeleted, filters.usersValidatedOnly]
  );

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAnalyticsApi.fetchUserActivity({
        ...queryBase,
        page,
        take: PAGE_SIZE,
      });
      setData(response);
    } catch (err: any) {
      setData(null);
      setError(err?.response?.data?.message ?? err?.message ?? 'Error al cargar usuarios.');
    } finally {
      setLoading(false);
    }
  }, [queryBase, page]);

  useEffect(() => {
    if (!isOpen) return;
    setScope('within');
    setPage(1);
    setExportError(null);
  }, [isOpen, windowDays]);

  useEffect(() => {
    if (!isOpen) return;
    loadActivity();
  }, [isOpen, loadActivity]);

  const totalPages = data ? Math.max(1, Math.ceil(data.itemCount / data.take)) : 1;

  const handleExport = async () => {
    setExportLoading(true);
    setExportError(null);
    try {
      const { blob, filename } = await adminAnalyticsApi.downloadUserActivityExport(queryBase);
      triggerBlobDownload(blob, filename);
    } catch (err: any) {
      setExportError(err?.message ?? 'Error al exportar.');
    } finally {
      setExportLoading(false);
    }
  };

  if (!isOpen) return null;

  const rangeLabel =
    data?.meta != null
      ? formatActivityRange(data.meta.asOf, data.meta.cutoffFrom, data.meta.cutoffTo)
      : null;

  const compareWarning = getLastLoginCompareWarning(compareTo);

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} zIndex={1000}>
      <div className={styles.modal} role="dialog" aria-labelledby="user-activity-title">
        <div className={styles.header}>
          <div>
            <h2 id="user-activity-title" className={styles.title}>
              {metricLabel}
            </h2>
            {rangeLabel && <p className={styles.subtitle}>Rango: {rangeLabel}</p>}
            <p className={styles.trackingNote}>{getActiveUsersDisclaimer()}</p>
            {compareWarning && <p className={styles.warning}>{compareWarning}</p>}
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className={styles.toolbar}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Alcance</span>
            <select
              className={styles.select}
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as UserActivityScope);
                setPage(1);
              }}
              disabled={loading || exportLoading}
            >
              {SCOPES.map((scopeKey) => (
                <option key={scopeKey} value={scopeKey}>
                  {USER_ACTIVITY_SCOPE_LABELS[scopeKey]}
                </option>
              ))}
            </select>
          </label>

          {data != null && (
            <span className={styles.count}>
              {data.itemCount.toLocaleString('es-AR')} usuario
              {data.itemCount === 1 ? '' : 's'}
            </span>
          )}

          <button
            type="button"
            className={styles.exportButton}
            onClick={handleExport}
            disabled={loading || exportLoading}
          >
            {exportLoading ? 'Exportando…' : 'Exportar CSV'}
          </button>
        </div>

        <div className={styles.body}>
          {error && <div className={styles.error}>{error}</div>}
          {exportError && <div className={styles.error}>{exportError}</div>}

          {loading && <LoadingSpinner message="Cargando usuarios..." />}

          {!loading && !error && data && data.items.length === 0 && (
            <p className={styles.empty}>No hay usuarios para este criterio.</p>
          )}

          {!loading && !error && data && data.items.length > 0 && (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Email / usuario</th>
                    <th>Último login</th>
                    <th>Validado</th>
                    <th>Tipo</th>
                    <th>Roles</th>
                    <th>Alta</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name || '—'}</td>
                      <td>{user.last_name || '—'}</td>
                      <td>{user.username || '—'}</td>
                      <td>{formatDateTime(user.last_login)}</td>
                      <td>{user.validated ? 'Sí' : 'No'}</td>
                      <td>{user.type || '—'}</td>
                      <td>{user.rols?.length ? user.rols.join(', ') : '—'}</td>
                      <td>{formatDateTime(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && data && data.itemCount > PAGE_SIZE && (
          <div className={styles.footer}>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </ModalOverlay>
  );
};

export default AnalyticsUserActivityModal;
